/** How many anchors use a connector, as the usage routes report it. */
interface ConnectorUsage {
  /** Null while the checks are not computed for the current model. */
  anchorCount?: number | null
  userDefinedCount: number
}

export type ConnectorKind = 'tensile' | 'shear'

const WORDING: Record<
  ConnectorKind,
  {
    headline: string
    connector: string
    thisConnector: string
    anchorSingular: string
    anchorPlural: string
    question: string
  }
> = {
  tensile: {
    headline: 'Zugverbindung',
    connector: 'Zugverbinder',
    thisConnector: 'Dieser Zugverbinder',
    anchorSingular: 'Zuganker',
    anchorPlural: 'Zugankern',
    question: 'Sind Sie sicher, dass Sie diese Zugverbindung löschen wollen?',
  },
  shear: {
    headline: 'Schubverbinder',
    connector: 'Schubverbinder',
    thisConnector: 'Dieser Schubverbinder',
    anchorSingular: 'Schubanker',
    anchorPlural: 'Schubankern',
    question: 'Sind Sie sicher, dass Sie diesen Schubverbinder löschen wollen?',
  },
}

const anchors = (count: number, kind: ConnectorKind) =>
  `${count} ${count === 1 ? WORDING[kind].anchorSingular : WORDING[kind].anchorPlural}`

/**
 * How many anchors use the connector: those of the computed checks, or — while those are not
 * computed — at least the ones it is fixed to by hand.
 */
const usedAnchorCount = (usage: ConnectorUsage | undefined): number =>
  Math.max(usage?.anchorCount ?? 0, usage?.userDefinedCount ?? 0)

/** The delete confirmation's headline; it says so when the connector is still in use. */
export const deleteConfirmationTitle = (
  usage: ConnectorUsage | undefined,
  kind: ConnectorKind = 'tensile',
): string => {
  const count = usedAnchorCount(usage)
  return count > 0
    ? `${WORDING[kind].headline} wird noch an ${anchors(count, kind)} verwendet – trotzdem löschen?`
    : `${WORDING[kind].headline} löschen?`
}

/** The delete confirmation's text: what happens to the anchors that use the connector. */
export const deleteConfirmationText = (
  usage: ConnectorUsage | undefined,
  kind: ConnectorKind = 'tensile',
): string => {
  const wording = WORDING[kind]
  if (usage?.userDefinedCount) {
    return `${wording.thisConnector} ist bei ${anchors(
      usage.userDefinedCount,
      kind,
    )} manuell festgelegt. Beim Löschen werden diese auf „Automatisch (Priorisierung)“ zurückgesetzt. ${
      wording.question
    }`
  }
  if (usedAnchorCount(usage) > 0) {
    return `Die betroffenen ${wording.anchorSingular} erhalten nach dem Löschen einen ${wording.connector} aus der Priorisierung. ${wording.question}`
  }
  return wording.question
}

/** The success message of a delete, naming the anchors reset to the prioritization. */
export const deletedConnectorMessage = (resetCount: number, kind: ConnectorKind): string =>
  resetCount > 0
    ? `${WORDING[kind].headline} gelöscht – ${resetCount} ${
        resetCount === 1
          ? `${WORDING[kind].anchorSingular} steht`
          : `${WORDING[kind].anchorSingular} stehen`
      } wieder auf „Automatisch (Priorisierung)“`
    : `${WORDING[kind].headline} erfolgreich gelöscht`
